import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  SUBSCRIPTION_GATE_EVENT,
  type SubscriptionGateEvent,
} from '@saas-kit/lib';

export function useSubscriptionGateEvent(): {
  event: SubscriptionGateEvent | null;
  dismiss: () => void;
} {
  const [event, setEvent] = useState<SubscriptionGateEvent | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SubscriptionGateEvent>).detail;
      if (detail) setEvent(detail);
    };
    window.addEventListener(SUBSCRIPTION_GATE_EVENT, handler);
    return () => window.removeEventListener(SUBSCRIPTION_GATE_EVENT, handler);
  }, []);
  return { event, dismiss: () => setEvent(null) };
}

interface Props {
  event: SubscriptionGateEvent;
  onDismiss: () => void;
}

export function SubscriptionGateBanner({ event, onDismiss }: Props): JSX.Element {
  const { t } = useTranslation('billing');
  const title =
    event.reason === 'required'
      ? t('gate.required.title')
      : t('gate.inactive.title');
  const body =
    event.reason === 'required'
      ? t('gate.required.body')
      : t('gate.inactive.body', { status: event.status ?? 'inactive' });

  return (
    <div className="flex h-full w-full items-center justify-between gap-3 bg-amber-500/15 px-4 text-sm text-amber-100">
      <div>
        <span className="font-medium">{title}</span> · {body}
      </div>
      <Link
        to="/billing"
        className="rounded-md border border-amber-300/40 px-3 py-1 text-amber-50 hover:bg-amber-400/15"
        onClick={onDismiss}
      >
        {t('gate.cta')}
      </Link>
    </div>
  );
}
